import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";

import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import validators, { normalizePostalCode } from "@tSurya/Common/Utilities/validations";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@tSurya/Overrides/Components/CustomerAddressFormFields";
import React from "react";
import { css } from "styled-components";

export interface CustomAddressModel extends BaseAddressModel {
    addressType: string;
    dockdoor: string;
}

interface OwnProps<Address extends BillToModel | ShipToModel> {
    address: Address;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    saveButtonTextOverride?: string;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>, address: Address) => void;
    extendedStyles?: CustomerAddressFormStyles;
}

interface State<Address extends BillToModel | ShipToModel> {
    address: Address;
    countryLookup?: CountryModel;
    formErrors: FormErrorsState;
    formSubmitAttempted: boolean;
    isSubmitting: boolean;
    addressType: string;
    dockdoor: string;
    county: string;
}

type FormErrorsState = {
    firstName?: React.ReactNode;
    lastName?: React.ReactNode;
    attention?: React.ReactNode;
    companyName?: React.ReactNode;
    address1?: React.ReactNode;
    address2?: React.ReactNode;
    address3?: React.ReactNode;
    address4?: React.ReactNode;
    country?: React.ReactNode;
    city?: React.ReactNode;
    state?: React.ReactNode;
    postalCode?: React.ReactNode;
    phone?: React.ReactNode;
    email?: React.ReactNode;
    fax?: React.ReactNode;
    addressType?: React.ReactNode;
    dockDoor?: React.ReactNode;
};

export interface CustomerAddressFormStyles {
    form?: InjectableCss;
    formFields?: CustomerAddressFormFieldStyles;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const customerAddressFormStyles: CustomerAddressFormStyles = {
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    submitButton: {
        css: css`
            &:disabled {
                background: #cccccc;
                border-color: #cccccc;
            }
        `,
    },
};

const styles = customerAddressFormStyles;
const StyledForm = getStyledWrapper("form");

const getRequiredErrorMessage = (fieldDisplay: AddressFieldDisplayModel) =>
    siteMessage("Field_Required", fieldDisplay.displayName);

export default class CustomerAddressForm<Address extends BillToModel | ShipToModel> extends React.Component<
    OwnProps<Address>,
    State<Address>
> {
    private styles: CustomerAddressFormStyles;

    constructor(props: OwnProps<Address>) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        const { address } = this.props;
        const countryLookup = address.country
            ? this.props.countries.find(c => c.abbreviation === address.country!.abbreviation)
            : this.props.countries[0];
        this.state = {
            address,
            countryLookup,
            formErrors: {},
            formSubmitAttempted: false,
            isSubmitting: false,
            addressType: address.properties.residentialFlag,
            dockdoor: address.properties.dockDoorRequired,
            county: address.properties.County || address.properties.county || "",
        };
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const propertyName = target.name;
        let errorMessage = "";
        let _value = "";
        const fieldDisplay = this.props.addressFieldDisplayCollection;

        if (propertyName === "phone" || propertyName === "fax") {
            if (propertyName === "phone" && target.value.trim().length > 0 && !validatePhone(target.value)) {
                errorMessage = siteMessage("Phone number must contain at least 10 digits.") as string;
            }
            const cleanedValue = target.value.replace(/\D/g, "");
            // Perform the formatting
            const formattedValue = cleanedValue.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            _value = formattedValue;
        } else if (propertyName === "postalCode") {
            const inputValue = target.value.trim();
            const countryAbbreviation = this.state.address.country?.abbreviation;
            const normalizedValue = normalizePostalCode(inputValue, countryAbbreviation);
            const isValidFormat = validators.postalCode(normalizedValue, countryAbbreviation);

            errorMessage =
                inputValue.length === 0
                    ? "Postal Code is required."
                    : !isValidFormat
                      ? (siteMessage("Invalid PostalCode") as string)
                      : "";

            _value = normalizedValue;
        } else if (propertyName === "firstName") {
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("cutSewAndRug_shipToNameRequired") as string)
                    : !validators.name(target.value)
                      ? (siteMessage("InvalidShipToName") as string)
                      : "";
            _value = target.value;
        } else if (propertyName === "lastName") {
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("Website_Registration_LastNameRequired") as string)
                    : !validators.name(target.value)
                      ? (siteMessage("CreateNewAccountInfo_InvalidLastName") as string)
                      : "";
            _value = target.value;
        } else if (propertyName === "city") {
            errorMessage =
                target.value.trim().length === 0
                    ? (siteMessage("displayOrder_cityRequired") as string)
                    : !validators.city(target.value)
                      ? (siteMessage("cutSewAndRug_Invalidcity") as string)
                      : "";
            _value = target.value;
        } else if (propertyName === "email") {
            if (target.value.trim().length > 0 && !validateEmail(target.value)) {
                errorMessage = siteMessage("cutSewAndRug_InvalidEmail") as string;
            }
            _value = target.value;
        } else if (propertyName === "address1") {
            if (target.value.trim().length === 0) {
                errorMessage = siteMessage("cutSewAndRug_streetOneRequired") as string;
            }
            _value = target.value;
        } else if (propertyName === "companyName") {
            errorMessage =
                !validators.company(target.value) && target.value.length > 0
                    ? (siteMessage("Address_invalidCompany") as string)
                    : "";
            _value = target.value;
        } else {
            _value = target.value;
        }

        this.setState(prevState => ({
            address: {
                ...prevState.address,
                [propertyName]: _value,
            },
            formErrors: {
                ...prevState.formErrors,
                [propertyName]: errorMessage,
            },
        }));
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const countryLookup = country
            ? this.props.countries.find(c => c.abbreviation === country.abbreviation)
            : undefined;
        const errorMessage = !country ? siteMessage("Country is required") : "";
        this.setState({
            address: {
                ...this.state.address,
                country,
                state: null,
            },
            countryLookup,
            formErrors: {
                ...this.state.formErrors,
                country: errorMessage,
            },
        });
    };

    handleStateChange = (_event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => {
        const error = !state ? siteMessage("OpenTradeAccount_stateRequired") : "";
        this.setState({
            address: {
                ...this.state.address,
                state,
            },
            formErrors: {
                ...this.state.formErrors,
                state: error,
            },
        });
    };

    handleCountyChange: TextFieldProps["onChange"] = event => {
        const value = event.target.value;
        this.setState(prevState => ({
            county: value,
            address: {
                ...prevState.address,
                properties: {
                    ...prevState.address.properties,
                    County: value,
                },
            },
        }));
    };

    handleAddressTypeChange = (event: React.ChangeEvent<HTMLSelectElement>, dockdoor: string) => {
        const errorMessage = event.target.value === "" ? siteMessage("Address Type is Required") : "";
        this.setState(prevState => ({
            address: {
                ...prevState.address,
                properties: {
                    ...prevState.address.properties,
                    residentialFlag: event.target.value,
                    dockDoorRequired: event.target.value === "0" ? "0" : "",
                },
            },
            formErrors: {
                ...prevState.formErrors,
                addressType: errorMessage,
            },
        }));
    };

    handleDockDoorChange = (event: React.ChangeEvent<HTMLSelectElement>, addressType: string) => {
        const errorMessage = event.target.value === "" ? siteMessage("Dock Door is Required") : "";
        this.setState(prevState => ({
            address: {
                ...prevState.address,
                properties: {
                    ...prevState.address.properties,
                    residentialFlag: addressType,
                    dockDoorRequired: event.target.value,
                },
            },
            formErrors: {
                ...prevState.formErrors,
                dockDoor: errorMessage,
            },
        }));
    };

    validateForm = () => {
        const fieldDisplay = this.props.addressFieldDisplayCollection;
        const validation = this.props.address.validation!;
        const { countries } = this.props;
        const {
            firstName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = this.state.address;
        const addressType = this.state.address.properties.residentialFlag;
        const dockDoor = this.state.address.properties.dockDoorRequired;

        const formErrors: FormErrorsState = {};
        if (fieldDisplay.firstName?.isVisible && validation.firstName?.isRequired && firstName.trim().length <= 0) {
            formErrors.firstName = getRequiredErrorMessage(fieldDisplay.firstName);
        } else if (firstName.trim().length > 0 && !validators.name(firstName)) {
            formErrors.firstName = siteMessage("Website_Registration_InvalidName");
        }

        if (fieldDisplay.address1?.isVisible && validation.address1?.isRequired && address1.trim().length <= 0) {
            formErrors.address1 = getRequiredErrorMessage(fieldDisplay.address1);
        }

        if (fieldDisplay.address2?.isVisible && validation.address2?.isRequired && address2.trim().length <= 0) {
            formErrors.address2 = getRequiredErrorMessage(fieldDisplay.address2);
        }

        if (fieldDisplay.address3?.isVisible && validation.address3?.isRequired && address3.trim().length <= 0) {
            formErrors.address3 = getRequiredErrorMessage(fieldDisplay.address3);
        }

        if (fieldDisplay.address4?.isVisible && validation.address4?.isRequired && address4.trim().length <= 0) {
            formErrors.address4 = getRequiredErrorMessage(fieldDisplay.address4);
        }

        if (countries.length > 1 && !country && fieldDisplay.country) {
            formErrors.country = getRequiredErrorMessage(fieldDisplay.country);
        }

        if (fieldDisplay.city?.isVisible && validation.city?.isRequired && city.trim().length <= 0) {
            formErrors.city = getRequiredErrorMessage(fieldDisplay.city);
        } else if (city.trim().length > 0 && !validators.city(city)) {
            formErrors.city = siteMessage("cutSewAndRug_Invalidcity");
        }

        const { countryLookup } = this.state;
        if (
            countries.length > 0 &&
            countryLookup?.states &&
            countryLookup.states.length > 0 &&
            !state &&
            fieldDisplay.state
        ) {
            formErrors.state = getRequiredErrorMessage(fieldDisplay.state);
        }

        if (fieldDisplay.postalCode?.isVisible && validation.postalCode?.isRequired && postalCode.trim().length <= 0) {
            formErrors.postalCode = getRequiredErrorMessage(fieldDisplay.postalCode);
        } else if (!validators.postalCode(postalCode, country?.abbreviation)) {
            formErrors.postalCode = siteMessage("Invalid PostalCode");
        }

        if (fieldDisplay.phone?.isVisible && validation.phone?.isRequired && phone.trim().length <= 0) {
            formErrors.phone = getRequiredErrorMessage(fieldDisplay.phone);
        } else if (phone.trim().length > 0 && !validatePhone(phone)) {
            formErrors.phone = siteMessage("AddressInfo_Phone_Validation");
        }

        if (fieldDisplay.email?.isVisible && validation.email?.isRequired && email.trim().length <= 0) {
            formErrors.email = getRequiredErrorMessage(fieldDisplay.email);
        } else if (email.trim().length > 0 && !validateEmail(email)) {
            formErrors.email = siteMessage("cutSewAndRug_InvalidEmail");
        }

        if (addressType === undefined || addressType === "") {
            formErrors.addressType = siteMessage("Address Type is Required");
        }

        if (addressType === "1" && dockDoor === "") {
            formErrors.dockDoor = siteMessage("Dock Door is Required");
        }

        this.setState({ formErrors });

        return formErrors;
    };

    scrollToFirstFormError = () => {
        const firstErrorElement = document.querySelector("[aria-invalid='true']");

        if (firstErrorElement) {
            const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
            if (!formFieldParent) {
                return;
            }
            formFieldParent.scrollIntoView();
        }
    };

    submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ formSubmitAttempted: true, isSubmitting: true });

        const formErrors = this.validateForm();

        this.setState({ formErrors }, () => {
            if (Object.keys(this.state.formErrors).length === 0) {
                const mergedAddress = { ...this.props.address, ...this.state.address };
                this.props.onSubmit(event, mergedAddress);
            } else {
                this.setState({
                    isSubmitting: false,
                });
                this.scrollToFirstFormError();
            }
        });
    };

    render() {
        const {
            firstName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = this.state.address;

        const { formErrors } = this.state;
        const { countries, extendedStyles } = this.props;
        const { validation } = this.props.address;
        const fieldDisplay = this.props.addressFieldDisplayCollection;

        if (!validation) {
            return null;
        }
        const isSaveDisabled =
            (validation.firstName!.isDisabled &&
                validation.address1!.isDisabled &&
                validation.address2!.isDisabled &&
                validation.address3!.isDisabled &&
                validation.address4!.isDisabled &&
                validation.country!.isDisabled &&
                validation.city!.isDisabled &&
                validation.postalCode!.isDisabled &&
                validation.phone!.isDisabled &&
                validation.fax!.isDisabled &&
                validation.email!.isDisabled) ||
            this.state.isSubmitting;

        return (
            <StyledForm {...styles.form} onSubmit={this.submitHandler} noValidate>
                <CustomerAddressFormFields
                    firstName={firstName}
                    address1={address1}
                    address2={address2}
                    address3={address3}
                    address4={address4}
                    country={country}
                    city={city}
                    state={state}
                    postalCode={postalCode}
                    phone={phone}
                    email={email}
                    fax={fax}
                    onChangeFirstName={this.handleInputChange}
                    onChangeLastName={this.handleInputChange}
                    onChangeAttention={this.handleInputChange}
                    onChangeCompanyName={this.handleInputChange}
                    onChangeAddress1={this.handleInputChange}
                    onChangeAddress2={this.handleInputChange}
                    onChangeAddress3={this.handleInputChange}
                    onChangeAddress4={this.handleInputChange}
                    onChangeCountry={this.handleCountryChange}
                    onChangeState={this.handleStateChange}
                    onChangeCity={this.handleInputChange}
                    onChangePostalCode={this.handleInputChange}
                    onChangePhone={this.handleInputChange}
                    onChangeEmail={this.handleInputChange}
                    onChangeFax={this.handleInputChange}
                    firstNameError={formErrors.firstName}
                    lastNameError={formErrors.lastName}
                    attentionError={formErrors.attention}
                    companyNameError={formErrors.companyName}
                    address1Error={formErrors.address1}
                    address2Error={formErrors.address2}
                    address3Error={formErrors.address3}
                    address4Error={formErrors.address4}
                    countryError={formErrors.country}
                    stateError={formErrors.state}
                    cityError={formErrors.city}
                    postalCodeError={formErrors.postalCode}
                    phoneError={formErrors.phone}
                    emailError={formErrors.email}
                    addressTypeError={formErrors.addressType}
                    dockDoorError={formErrors.dockDoor}
                    faxError={formErrors.fax}
                    countries={countries}
                    validation={validation}
                    fieldDisplay={fieldDisplay}
                    extendedStyles={extendedStyles?.formFields}
                    addressType={this.state.addressType}
                    dockdoor={this.state.dockdoor}
                    county={this.state.county}
                    onChangeCounty={this.handleCountyChange}
                    onChangeAddressType={this.handleAddressTypeChange}
                    onChangeDockDoor={this.handleDockDoorChange}
                />
                <StyledWrapper {...this.styles.formButtonsWrapper}>
                    {this.props.onCancel && (
                        <Button
                            {...this.styles.cancelButton}
                            type="button"
                            onClick={this.props.onCancel}
                            data-test-selector="customerAddressForm_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    )}

                    <Button {...this.styles.submitButton} type="submit" data-test-selector="customerAddressForm_save">
                        {this.props.saveButtonTextOverride ?? translate("Save")}
                    </Button>
                </StyledWrapper>
            </StyledForm>
        );
    }
}
